import { authGet } from '../../lib';
import { showError } from '../features';
import { addResource, updateResource } from '../asyncActions';
import { AppThunk, NormalizeReturn } from '../types';
import {
  BasicAssignedTransferJobsIdNamePair,
  EditInventoryItemRequest,
  InventoryItemDetailsFilterParams,
  InventoryLocationItemDetailsResponse,
  InventoryTransferItemsStateResponse,
  InventoryTransferJobAPI,
  InventoryTransferJobRequest,
  InventoryTransferTypes,
  PauseInventoryTransferJobRequest,
  ScanInventoryTransferItemRequest,
  ScanInventoryTransferItemResponse,
  TransferInventoryJobDataResponse,
  TransferInventoryRequest,
  InventoryTransferItemRemainingResponse,
  WarehouseLocationInventoryItemAPI,
  InventoryItemHoldsParams,
  InventoryItemHoldsResponse,
  PlaceInventoryOnHoldRequest,
  AddPickJobRequest,
  AddPickJobResponse,
  EditPickJobAllocations,
  InventoryTransferLocationAPI,
  InventoryTransferIssueAPI,
  OrderOwnershipQuantityResponse,
  MissingPickData,
  CompletePickJobRequest,
} from '../../types/inventoryTypes';
import { FulfilledServiceAssigneeTaskType, IdNamePair } from '../../types';

interface InventoryTransferItemsStateParams {
  taskType?: FulfilledServiceAssigneeTaskType;
  assetVehicleID?: string | number;
  userID?: string | number;
}

interface InventoryTransferItemRemainingParams {
  taskType?: FulfilledServiceAssigneeTaskType;
  warehouseAssetVehicleID?: string | number;
  locationName?: string;
}

export function assignTransferInventoryItem(
  payload: InventoryTransferJobRequest,
  shouldSetUILoading: boolean,
  shouldShowErrorMessage = true,
): AppThunk<Promise<NormalizeReturn<InventoryTransferJobAPI>>> {
  return async (dispatch) => {
    const { data, error } = await dispatch(
      addResource<InventoryTransferJobAPI, InventoryTransferJobRequest>({
        baseUrl: '/inventory/transfer-items/assign',
        payload,
        message: 'Items successfully assigned',
        shouldSetUILoading,
        shouldShowErrorMessage,
      }),
    );
    if (error) {
      return { error };
    }
    return { data };
  };
}

export function transferInventoryItem(
  payload: TransferInventoryRequest,
  shouldSetUILoading: boolean,
  shouldShowErrorMessage = true,
): AppThunk<Promise<NormalizeReturn<WarehouseLocationInventoryItemAPI>>> {
  return async (dispatch) => {
    const { data, error } = await dispatch(
      addResource<WarehouseLocationInventoryItemAPI, TransferInventoryRequest>({
        baseUrl: '/inventory/transfer-items',
        payload,
        message: 'Items successfully updated',
        shouldSetUILoading,
        shouldShowErrorMessage,
      }),
    );
    if (error) {
      return { error };
    }
    return { data };
  };
}

export function getTransferOrderOwnership({
  warehouseLocationID,
  customerItemID,
  receiptID,
  moveableUnitID,
}: {
  warehouseLocationID: number | string;
  customerItemID?: number | string;
  receiptID?: number | string;
  moveableUnitID?: number | string | null;
}): AppThunk<Promise<NormalizeReturn<Array<OrderOwnershipQuantityResponse>>>> {
  return async () => {
    const { data, error } = await authGet<
      Array<OrderOwnershipQuantityResponse>
    >([
      'inventory/transfer-items/order-ownership',
      { warehouseLocationID, customerItemID, receiptID, moveableUnitID },
    ]);
    if (error) return { error };
    return { data };
  };
}

export function addInventoryTransferIssue(
  inventoryTransferId: number,
  payload: InventoryTransferIssueAPI,
  shouldSetUILoading: boolean,
): AppThunk<Promise<NormalizeReturn<InventoryTransferIssueAPI>>> {
  return async (dispatch) => {
    const { data, error } = await dispatch(
      addResource<InventoryTransferIssueAPI, InventoryTransferIssueAPI>({
        baseUrl: `/inventory/transfer-items/${inventoryTransferId}/issue`,
        payload,
        message: 'Issue successfully added',
        shouldSetUILoading,
      }),
    );
    if (error) {
      return { error };
    }
    return { data };
  };
}

export function editInventoryItem(
  payload: EditInventoryItemRequest,
  shouldSetUILoading: boolean,
): AppThunk<Promise<NormalizeReturn<WarehouseLocationInventoryItemAPI>>> {
  return async (dispatch) => {
    const { data, error } = await dispatch(
      addResource<WarehouseLocationInventoryItemAPI, EditInventoryItemRequest>({
        baseUrl: '/inventory/edit-inventory',
        payload,
        message: 'Item successfully updated',
        shouldSetUILoading,
      }),
    );
    if (error) {
      return { error };
    }
    return { data };
  };
}

export function getAssignedStockMoveTasks({
  warehouseId,
  transferType,
  taskType,
}: {
  warehouseId: string | number;
  transferType: InventoryTransferTypes;
  taskType?: FulfilledServiceAssigneeTaskType;
}): AppThunk<
  Promise<NormalizeReturn<Array<BasicAssignedTransferJobsIdNamePair>>>
> {
  return async (dispatch) => {
    const { data, error } = await authGet<
      Array<BasicAssignedTransferJobsIdNamePair>
    >([
      'inventory/assigned-stock-move',
      {
        warehouseID: warehouseId,
        taskType,
        transferType,
      },
    ]);
    if (error) {
      return { error };
    }
    return { data };
  };
}

export function scanInventoryItemOriginLocation(
  inventoryTransferId: number,
  payload: ScanInventoryTransferItemRequest,
): AppThunk<Promise<NormalizeReturn<ScanInventoryTransferItemResponse>>> {
  return async (dispatch) => {
    const { data, error } = await dispatch(
      addResource<
        ScanInventoryTransferItemResponse,
        ScanInventoryTransferItemRequest
      >({
        baseUrl: `inventory/transfer-items/${inventoryTransferId}/scan-item/origin`,
        payload,
        shouldSetUILoading: true,
        shouldShowErrorMessage: false,
      }),
    );
    if (error) {
      return { error };
    }
    return { data };
  };
}

export function scanInventoryItemDestinationLocation(
  inventoryTransferId: number,
  payload: ScanInventoryTransferItemRequest,
): AppThunk<Promise<NormalizeReturn<ScanInventoryTransferItemResponse>>> {
  return async (dispatch) => {
    const { data, error } = await dispatch(
      addResource<
        ScanInventoryTransferItemResponse,
        ScanInventoryTransferItemRequest
      >({
        baseUrl: `inventory/transfer-items/${inventoryTransferId}/scan-item/destination`,
        payload,
        shouldSetUILoading: true,
        shouldShowErrorMessage: false,
      }),
    );
    if (error) {
      return { error };
    }
    return { data };
  };
}

export function getInventoryTransferItemsState({
  inventoryTransferId,
  filters,
}: {
  inventoryTransferId: number | string;
  filters?: InventoryTransferItemsStateParams;
}): AppThunk<Promise<NormalizeReturn<InventoryTransferItemsStateResponse>>> {
  return async (dispatch) => {
    const { data, error } = await authGet<InventoryTransferItemsStateResponse>([
      `inventory/transfer-items/${inventoryTransferId}/items-stats`,
      filters,
    ]);
    if (error) {
      dispatch(
        showError({
          message: error.message || error.title || 'An error occurred',
        }),
      );
      return { error };
    }
    return { data };
  };
}

export function getVehicleInventoryItemsAsync({
  inventoryTransferId,
  filters,
}: {
  inventoryTransferId: number | string;
  filters: InventoryTransferItemRemainingParams;
}): AppThunk<Promise<NormalizeReturn<InventoryTransferItemRemainingResponse>>> {
  return async (dispatch) => {
    const { data, error } =
      await authGet<InventoryTransferItemRemainingResponse>([
        `inventory/transfer-items/${inventoryTransferId}/remaining-items`,
        filters,
      ]);
    if (error) {
      dispatch(
        showError({
          message: error.message || error.title || 'An error occurred',
        }),
      );
      return { error };
    }
    return { data };
  };
}

export function getInventoryTransferDataDetails({
  inventoryTransferId,
  originWarehouseLocationID,
  originWarehouseLocationName,
  taskType,
}: {
  inventoryTransferId: number;
  originWarehouseLocationID?: string | number;
  originWarehouseLocationName?: string;
  taskType?: FulfilledServiceAssigneeTaskType;
}): AppThunk<Promise<NormalizeReturn<TransferInventoryJobDataResponse>>> {
  return async (dispatch) => {
    const { data, error } = await authGet<TransferInventoryJobDataResponse>([
      `inventory/transfer-items/${inventoryTransferId}/transfer-data`,
      { originWarehouseLocationID, originWarehouseLocationName, taskType },
    ]);
    if (error) {
      dispatch(
        showError({
          message: error.message || error.title || 'An error occurred',
        }),
      );
      return { error };
    }
    return { data };
  };
}

export function getInventoryTransferBasicAssignedAisles(
  inventoryTransferId: number,
): AppThunk<Promise<NormalizeReturn<Array<IdNamePair>>>> {
  return async (dispatch) => {
    const { data, error } = await authGet<Array<IdNamePair>>([
      `inventory/transfer-items/${inventoryTransferId}/assigned-aisles/basic`,
    ]);
    if (error) {
      dispatch(
        showError({
          message: error.message || error.title || 'An error occurred',
        }),
      );
      return { error };
    }
    return { data };
  };
}

export function getInventoryTransferBasicAssignedAisleLocations(
  inventoryTransferId: number,
  aisleId: number,
): AppThunk<Promise<NormalizeReturn<Array<IdNamePair>>>> {
  return async (dispatch) => {
    const { data, error } = await authGet<Array<IdNamePair>>([
      `inventory/transfer-items/${inventoryTransferId}/assigned-aisles/${aisleId}/assigned-locations/basic`,
    ]);
    if (error) {
      dispatch(
        showError({
          message: error.message || error.title || 'An error occurred',
        }),
      );
      return { error };
    }
    return { data };
  };
}

/**
 * Origin locations on the job that are not in any aisle, such as the non-specific location.
 * These never appear in the aisle list, so the picker reaches them from the aisle step instead.
 */
export function getInventoryTransferBasicNonAisleLocations(
  inventoryTransferId: number,
): AppThunk<Promise<NormalizeReturn<Array<IdNamePair>>>> {
  return async () => {
    const { data, error } = await authGet<Array<IdNamePair>>([
      `inventory/transfer-items/${inventoryTransferId}/assigned-locations/non-aisle/basic`,
    ]);
    // Deliberately silent: this augments the aisle list rather than driving it. Surfacing a
    // toast here would fire on every aisle screen if the portal ships ahead of the API.
    if (error) {
      return { error };
    }
    return { data };
  };
}

export function getInventoryItemDetails(
  locationIdOrName: string | number,
  customerItemId: string | number,
  params?: InventoryItemDetailsFilterParams,
): AppThunk<Promise<NormalizeReturn<WarehouseLocationInventoryItemAPI>>> {
  return async (dispatch) => {
    const { data, error } = await authGet<WarehouseLocationInventoryItemAPI>([
      `inventory/stock/locations/${locationIdOrName}/items/${customerItemId}/details`,
      params,
    ]);
    if (error) {
      dispatch(
        showError({
          message: error.message || error.title || 'An error occurred',
        }),
      );
      return { error };
    }
    return { data };
  };
}

export function pauseMoveInventoryJob({
  inventoryTransferId,
  payload,
}: {
  inventoryTransferId: number | string;
  payload: PauseInventoryTransferJobRequest;
}): AppThunk<Promise<NormalizeReturn>> {
  return async (dispatch) => {
    const { data, error } = await dispatch(
      addResource<any, PauseInventoryTransferJobRequest>({
        baseUrl: `inventory/transfer-items/${inventoryTransferId}/pause`,
        payload,
        message: 'Successfully paused job',
        shouldSetUILoading: true,
      }),
    );
    if (error) {
      return { error };
    }
    return { data };
  };
}

export function GetInventoryLocationItemDetails(
  locationIdOrName: string | number,
  warehouseId: string | number,
): AppThunk<Promise<NormalizeReturn<InventoryLocationItemDetailsResponse>>> {
  return async (dispatch) => {
    const { data, error } = await authGet<InventoryLocationItemDetailsResponse>(
      [
        `inventory/stock/locations/${locationIdOrName}/details`,
        { warehouseId },
      ],
    );
    if (error) {
      dispatch(
        showError({
          message: error.message || error.title || 'An error occurred',
        }),
      );
      return { error };
    }
    return { data };
  };
}

export function getInventoryItemHolds(
  params: InventoryItemHoldsParams,
): AppThunk<Promise<NormalizeReturn<Array<InventoryItemHoldsResponse>>>> {
  return async (dispatch) => {
    const { data, error } = await authGet<Array<InventoryItemHoldsResponse>>([
      `inventory/stock/items/holds`,
      params,
    ]);
    if (error) {
      dispatch(
        showError({
          message: error.message || error.title || 'An error occurred',
        }),
      );
      return { error };
    }
    return { data };
  };
}

export function placeInventoryOnHold(
  payload: PlaceInventoryOnHoldRequest,
): AppThunk<Promise<NormalizeReturn<InventoryItemHoldsResponse>>> {
  return async (dispatch) => {
    const { data, error } = await dispatch(
      addResource<InventoryItemHoldsResponse, PlaceInventoryOnHoldRequest>({
        baseUrl: '/inventory/place-on-hold',
        payload,
        shouldSetUILoading: true,
        shouldShowErrorMessage: true,
      }),
    );
    if (error) {
      return { error };
    }
    return { data };
  };
}

export function addPickJob(
  payload: AddPickJobRequest,
): AppThunk<Promise<NormalizeReturn<AddPickJobResponse>>> {
  return async (dispatch) => {
    const { data, error } = await dispatch(
      addResource<AddPickJobResponse, AddPickJobRequest>({
        baseUrl: '/pickJobs',
        payload,
        shouldSetUILoading: true,
        shouldShowErrorMessage: true,
      }),
    );
    if (error) {
      return { error };
    }
    return { data };
  };
}

export function editPickJobAllocations(
  pickJobId: number | string,
  payload: EditPickJobAllocations,
): AppThunk<Promise<NormalizeReturn<Array<InventoryTransferLocationAPI>>>> {
  return async (dispatch) => {
    const { data, error } = await dispatch(
      updateResource<
        Array<InventoryTransferLocationAPI>,
        EditPickJobAllocations
      >({
        baseUrl: `/pickJobs/${pickJobId}/allocations`,
        payload,
        shouldSetUILoading: true,
        shouldShowErrorMessage: true,
      }),
    );
    if (error) {
      return { error };
    }
    return { data };
  };
}

export function completePickJob(
  pickJobId: number | string,
  payload: CompletePickJobRequest,
  shouldSetUILoading = true,
  shouldShowErrorMessage = true,
): AppThunk<Promise<NormalizeReturn<Array<MissingPickData>>>> {
  return async (dispatch) => {
    const { data, error } = await dispatch(
      addResource<Array<MissingPickData>, CompletePickJobRequest>({
        baseUrl: `pickJobs/${pickJobId}/complete`,
        payload,
        shouldSetUILoading,
        shouldShowErrorMessage,
        message: 'Pick job completed successfully',
      }),
    );
    if (error) {
      return { error };
    }
    return { data };
  };
}

export function returnPalletToStaging(
  moveablePalletId: number | string,
): AppThunk<Promise<NormalizeReturn>> {
  return async (dispatch) => {
    const { data, error } = await dispatch(
      addResource({
        baseUrl: `inventory/moveable-pallets/${moveablePalletId}/remove-workstation`,
        payload: {},
        shouldSetUILoading: true,
        shouldShowErrorMessage: true,
        message: 'Pallet returned to staging successfully',
      }),
    );
    if (error) {
      return { error };
    }
    return { data };
  };
}
